/**
 * React Query hook for managing manual cost-basis overrides
 *
 * Allows users to manually set or revert cost-basis values for transactions
 * Supports querying existing manual values and mutating them
 */

import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { authorizedAxios } from "@moonscape/shared";
import { useAuth0 } from "@auth0/auth0-react";

export interface CostBasisData {
  txn_id: number;
  manual_cost_basis: number | null;
  manual_cost_basis_set_at: string | null;
  cost_basis_note: string | null;
}

export interface SetCostBasisParams {
  txn_id: number;
  cost_basis: number;
  note?: string;
}

/**
 * Hook to fetch manual cost-basis data for a transaction
 */
export const useCostBasis = (txnId: number | null) => {
  const { getAccessTokenSilently } = useAuth0();
  const queryClient = useQueryClient();

  // Query for fetching cost-basis data
  const query = useQuery<CostBasisData>({
    queryKey: ["costBasis", txnId],
    queryFn: async () => {
      if (!txnId) {
        throw new Error("Transaction ID is required");
      }

      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).get(
        `/transactions/${txnId}/cost-basis`
      );

      return response.data;
    },
    enabled: !!txnId,
    staleTime: 1000 * 60 * 5, // Cache for 5 minutes
    retry: 2,
  });

  // Mutation for setting cost-basis
  const setCostBasisMutation = useMutation({
    mutationFn: async (params: SetCostBasisParams) => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).post(
        `/transactions/${params.txn_id}/cost-basis`,
        {
          cost_basis: params.cost_basis,
          note: params.note,
        }
      );
      return response.data;
    },
    onSuccess: (data, variables) => {
      // Invalidate and refetch cost-basis query
      queryClient.invalidateQueries({ queryKey: ["costBasis", variables.txn_id] });
      // Also invalidate tax events to reflect updated calculations
      queryClient.invalidateQueries({ queryKey: ["tax", "events"] });
      queryClient.invalidateQueries({ queryKey: ["audit", "events"] });
    },
  });

  // Mutation for reverting cost-basis (DELETE)
  const revertCostBasisMutation = useMutation({
    mutationFn: async (txn_id: number) => {
      const token = await getAccessTokenSilently();
      const response = await authorizedAxios(token).delete(
        `/transactions/${txn_id}/cost-basis`
      );
      return response.data;
    },
    onSuccess: (data, txn_id) => {
      // Invalidate and refetch cost-basis query
      queryClient.invalidateQueries({ queryKey: ["costBasis", txn_id] });
      // Also invalidate tax events to reflect updated calculations
      queryClient.invalidateQueries({ queryKey: ["tax", "events"] });
      queryClient.invalidateQueries({ queryKey: ["audit", "events"] });
    },
  });

  return {
    costBasisData: query.data,
    isLoading: query.isLoading,
    isError: query.isError,
    error: query.error,
    setCostBasis: (params: SetCostBasisParams) => setCostBasisMutation.mutate(params),
    revertCostBasis: (txn_id: number) => revertCostBasisMutation.mutate(txn_id),
    isUpdating: setCostBasisMutation.isPending || revertCostBasisMutation.isPending,
    setError: setCostBasisMutation.error,
    revertError: revertCostBasisMutation.error,
  };
};
